import React, {
  createContext,
  useState,
  useCallback,
  useContext,
  ReactNode,
  useEffect,
} from 'react'
import { getAccessToken, logoutUser } from '../api/authService'
import { getOwnPhoto, getOwnUser } from '../api/userService'
import { useToast } from './ToastContext'
// Define the authentication status enum
export enum AuthStatus {
  LOADING = 'loading',
  AUTHENTICATED = 'authenticated',
  UNAUTHENTICATED = 'unauthenticated',
}

// Define the user type. Access is decided by permission ids (never role names);
// roles are kept only for display.
export interface User {
  email: string
  firstName: string
  lastName: string
  permissionIds: number[]
  roles: string[]
  // An object URL for their Entra profile photo; absent when they have none
  // (most people), and the avatar shows their initials instead.
  photoUrl?: string
}

// Define the context type
export interface AuthContextType {
  user: User | null
  status: AuthStatus
  login: (code: string) => void
  logout: () => void
}

// Create the context
const AuthContext = createContext<AuthContextType | null>(null)

// Create the Auth Provider component
type AuthProviderProps = {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [status, setStatus] = useState<AuthStatus>(AuthStatus.LOADING)
  const { showError } = useToast()

  // The photo arrives after sign-in, never holding it up; failing is silent.
  const loadPhoto = useCallback(async () => {
    const photoUrl = await getOwnPhoto()
    if (photoUrl) setUser(u => (u ? { ...u, photoUrl } : u))
  }, [])

  // Release the previous photo's object URL when it is replaced or dropped.
  const photoUrl = user?.photoUrl
  useEffect(() => {
    return () => {
      if (photoUrl) URL.revokeObjectURL(photoUrl)
    }
  }, [photoUrl])

  const login = async (code: string): Promise<void> => {
    try {
      setStatus(AuthStatus.LOADING)
      await getAccessToken(code)

      const newUser = await getOwnUser()
      setUser(newUser)

      setStatus(AuthStatus.AUTHENTICATED)
      loadPhoto()
    } catch (error) {
      console.error('Error during authorisation:', error)
      setStatus(AuthStatus.UNAUTHENTICATED)
      throw error
    }
  }

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const user = await getOwnUser()
        setUser(user)
        setStatus(AuthStatus.AUTHENTICATED)
        loadPhoto()
      } catch (error) {
        setStatus(AuthStatus.UNAUTHENTICATED)
        // A 401 just means "not signed in yet" (expected). Anything else — a server
        // error or an unreachable backend — is a real failure worth signalling.
        const httpStatus = (error as { response?: { status?: number } })?.response
          ?.status
        if (httpStatus !== 401) {
          showError('Could not reach the server. Please try again.')
        }
      }
    }
    checkAuth()
  }, [showError, loadPhoto])
  const logout = async (): Promise<void> => {
    setStatus(AuthStatus.LOADING)
    try {
      await logoutUser()
    } catch (error) {
      // Non-fatal: the server call failed, but still clear the local session so the
      // user is signed out on this device.
      console.error('Error during logout:', error)
    }
    setUser(null)
    setStatus(AuthStatus.UNAUTHENTICATED)
  }
  const value = {
    user,
    status,
    login,
    logout,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// Create the useAuth hook
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)

  if (context === null) {
    throw new Error('useAuth must be used within an AuthProvider')
  }

  return context
}
